import { useCustomerContext } from "../../utils/CustomerContext";
import "./Customer.css";
import { CustomerCorpora } from "./CustomerCorpora";
import { CustomerHeader } from "./CustomerHeader";
import { CustomerJobs } from "./CustomerJobs";
import { CustomerPlan } from "./CustomerPlan";
import { TabbedRoutes } from "../tabbedRoutes";
import { CustomerStats } from "./CustomerStats";
import { VuiSpacer, VuiSpinner, VuiText } from "@vectara/vectara-ui";
import { CustomerEncoders } from "./CustomerEncoders";
import { CustomerPrompts } from "./CustomerPrompts";
import { CustomerBilling } from "./CustomerBilling";
import { useLoginContext } from "../../utils/LoginContext";
import { CustomerStatus } from "../../utils/commonTypes";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getCustomerById } from "../../api/api";

export const Customer = () => {
  const { id } = useParams<{ id: string }>();
  const urlCustomerId = id ? Number(id) : NaN;
  const { customer, updateCustomer } = useCustomerContext();
  const { authUser, authToken, isAuthenticated } = useLoginContext();
  const [loadError, setLoadError] = useState<string | null>(null);
  const isContextStale = !Number.isNaN(urlCustomerId) && customer.customerId !== urlCustomerId;

  useEffect(() => {
    if (Number.isNaN(urlCustomerId) || !isAuthenticated || !authToken) return;
    if (customer.customerId === urlCustomerId) return;

    let cancelled = false;
    setLoadError(null);
    getCustomerById(authToken, urlCustomerId)
      .then((fetched) => {
        if (cancelled) return;
        if (fetched) {
          updateCustomer(fetched);
        } else {
          setLoadError(`Customer ${urlCustomerId} not found.`);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        console.error(e);
        setLoadError(`Failed to load customer ${urlCustomerId}.`);
      });

    return () => {
      cancelled = true;
    };
  }, [urlCustomerId, isAuthenticated, authToken, customer.customerId, updateCustomer]);

  if (Number.isNaN(urlCustomerId)) {
    return <VuiText className="ml-10 mt-5">Invalid customer id.</VuiText>;
  }
  if (loadError) {
    return <VuiText className="ml-10 mt-5">{loadError}</VuiText>;
  }
  if (isContextStale) {
    return (
      <div className="flex ml-10 mt-5">
        <VuiSpinner size="s" />
      </div>
    );
  }

  const DEFAULT_TAB_ADDRESS = `/customer/${customer.customerId}/corpora`;

  const getFilteredTabs = () => {
    const baseTabs = [
      {
        href: "corpora",
        title: "Corpora",
        component: <CustomerCorpora />
      },
      {
        href: "stats",
        title: "Stats",
        component: <CustomerStats />
      },
      {
        href: "plan",
        title: "Customer Plan",
        component: <CustomerPlan />
      },
      {
        href: "jobs",
        title: "Jobs",
        component: <CustomerJobs />
      },
      {
        href: "encoders",
        title: "Encoders",
        component: <CustomerEncoders />
      },
      {
        href: "prompts",
        title: "Prompts",
        component: <CustomerPrompts />
      }
    ];

    // Add billing tab if user has permission
    if (authUser.permissions.includes("PERMISSION_VIEW_BILLING")) {
      baseTabs.push({
        href: "billing",
        title: "Billing",
        component: <CustomerBilling />
      });
    }

    // Filter tabs based on customer status
    if (customer.status === CustomerStatus.MDEL) {
      // For MDEL customers, show all tabs except Jobs
      return baseTabs.filter(tab => tab.href !== "jobs");
    } else if (customer.status === CustomerStatus.DEL) {
      // For DEL customers, show only Stats, CustomerPlan and Billing tabs
      const allowedTabs = ["stats", "plan", "billing"];
      return baseTabs.filter(tab => allowedTabs.includes(tab.href));
    }

    // For ACT and EXP customers, show all tabs
    return baseTabs;
  };

  const customerTabbedRoutes = getFilteredTabs();

  return (
    <div className="flex flex-col ml-10 mt-5">
      <CustomerHeader customer={customer} />
      <VuiSpacer size="l" />
      <TabbedRoutes routes={customerTabbedRoutes} defaultRoute={DEFAULT_TAB_ADDRESS} />
    </div>
  );
};
